import { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { useOktaAuth } from '@okta/okta-react';
import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';

import { DEFAULT_GET_STALE_TIME } from '@frontend/api/constants';
import { getAuthStatus } from '@frontend/api/services/authStatusService';
import { getCurrentOpalUser } from '@frontend/api/services/OpaluserService';
import { OpalAuth, opalAuthAtom, opalUserAtom } from '@frontend/atoms';
import { OPAL_BACKEND_URL, RUNTIME_ENV, WS_GATEWAY_URL } from '@frontend/envConfig';
import { useInstanceId } from '@frontend/hooks';
import { getOrRenewOptiIdToken } from '@frontend/oktaAuth';
import { sendSentryError, sendSentryMessage } from '@frontend/utils/sendSentryError';

import AccessDenied from './AccessDenied';
import AtomsHydrator from './AtomsHydrator';
import FullScreenLoader from './FullScreenLoader';

// @ts-expect-error - External module import
const OpalAuthPromise = import('OpalChat/OpalAuth')
  .then(m => m.default as OpalAuth)
  .catch(() => null);

const RequireAuth = ({ children }: { children: JSX.Element }): JSX.Element => {
  const { authState, oktaAuth } = useOktaAuth();
  const { instanceId } = useInstanceId();
  const [opalAuth, setOpalAuth] = useAtom(opalAuthAtom);
  const navigate = useNavigate();

  useEffect(() => {
    const handleError = (error: Error) => {
      sendSentryMessage('TokenManager error', { error: error.message }, 'error');
    };

    oktaAuth.tokenManager.on('error', handleError);

    return () => {
      oktaAuth.tokenManager.off('error');
    };
  }, [oktaAuth]);

  const {
    data: user,
    isLoading: userLoading,
    isError: hasUserFetchError,
    error: userFetchError,
  } = useQuery({
    queryKey: ['current-user', instanceId],
    queryFn: async () => {
      return getCurrentOpalUser(instanceId);
    },
    enabled: Boolean(authState?.isAuthenticated),
    staleTime: DEFAULT_GET_STALE_TIME,
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    meta: { persist: true },
  });

  const {
    data: authStatus,
    isSuccess: authStatusSuccess,
    isError: authStatusError,
    dataUpdatedAt: authStatusUpdatedAt,
  } = useQuery({
    queryKey: ['auth-status', instanceId],
    queryFn: () => getAuthStatus(instanceId ?? ''),
    enabled: Boolean(authState?.isAuthenticated) && Boolean(instanceId),
    staleTime: DEFAULT_GET_STALE_TIME,
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    meta: { persist: true },
  });
  // OpalAuth init waits for this query to settle (success or error) so it can be
  // seeded from the cached status and skip its own /auth/status call.
  const authStatusSettled = authStatusSuccess || authStatusError;
  // Only seed OpalAuth from the cache when the data is still within the stale
  // window. Stale data would let OpalAuth skip its own /auth/status fetch and
  // run the session with outdated operations/preferences.
  const authStatusFresh =
    authStatusSuccess && Date.now() - authStatusUpdatedAt < DEFAULT_GET_STALE_TIME;
  // Ref so the initializeOpalAuth effect can read the latest authStatus without
  // re-running every time it revalidates (which would reset opalAuth unnecessarily).
  const authStatusRef = useRef(authStatus);
  authStatusRef.current = authStatus;

  useEffect(() => {
    const login = async () => {
      const originalUri = toRelativeUrl(window.location.href, window.location.origin);
      oktaAuth.setOriginalUri(originalUri);
      await oktaAuth.signInWithRedirect();
    };

    if (!authState) {
      return;
    }
    if (!authState.isAuthenticated) {
      login().catch(sendSentryError);
    }
  }, [authState, oktaAuth, authState?.isAuthenticated]);

  useEffect(() => {
    if (RUNTIME_ENV !== 'production') {
      localStorage.setItem('ai-chat:BASE_URL', OPAL_BACKEND_URL || '');
      localStorage.setItem('ai-chat:SOCKET_URL', WS_GATEWAY_URL || '');
    }
  }, []);

  // Build OpalAuth off the URL instance id (available immediately) and the
  // settled auth-status query, independent of the current-user fetch. This lets
  // OpalAuth initialize concurrently with current-user instead of after it; the
  // screen still waits for both, but overlapping them removes a network
  // round-trip from the critical path. The url instance id is the same one
  // current-user is fetched with, so user.instance.instance_id matches it.
  const initializedInstanceRef = useRef<string | null>(null);
  useEffect(() => {
    if (!instanceId || !authState?.isAuthenticated || !authStatusSettled) {
      return;
    }
    if (initializedInstanceRef.current === instanceId) {
      return;
    }
    initializedInstanceRef.current = instanceId;

    const initializeOpalAuth = async () => {
      const OpalAuthModule = await OpalAuthPromise;
      if (!OpalAuthModule) {
        initializedInstanceRef.current = null; // allow a later retry
        return;
      }
      const accessToken = await getOrRenewOptiIdToken();
      const cachedAuthStatus = authStatusFresh ? authStatusRef.current : undefined;
      const initialAuthData = cachedAuthStatus
        ? {
            id: cachedAuthStatus.id,
            email: cachedAuthStatus.email,
            name: cachedAuthStatus.name,
            customerId: cachedAuthStatus.instance_details.customer_id,
            masterCustomerId: cachedAuthStatus.instance_details.master_customer_id,
            cmpOrgSsoId: cachedAuthStatus.instance_details.cmp_org_sso_id,
            productScope: cachedAuthStatus.product_scope,
            productId: cachedAuthStatus.instance_details.product_id,
            productSku: cachedAuthStatus.instance_details.product_sku,
            operations: cachedAuthStatus.instance_details.operations,
            connectedOpalInstanceId: cachedAuthStatus.instance_details.connected_opal_instance_id,
            accessToken: accessToken ?? '',
            instanceId,
            hostedRegion: cachedAuthStatus.instance_details.hosted_region,
            userPreferences: cachedAuthStatus.user_preferences
              ? { fontSize: cachedAuthStatus.user_preferences.font_size }
              : undefined,
          }
        : undefined;
      const opalAuth = OpalAuthModule.create(
        instanceId,
        accessToken ?? '',
        getOrRenewOptiIdToken,
        initialAuthData
      );
      await opalAuth.initAuth();
      setOpalAuth(opalAuth);
    };
    initializeOpalAuth().catch(error => {
      initializedInstanceRef.current = null; // allow a later retry
      sendSentryError(error);
    });
  }, [instanceId, authState?.isAuthenticated, authStatusSettled, setOpalAuth]);

  useEffect(() => {
    if (
      hasUserFetchError &&
      userFetchError instanceof AxiosError &&
      userFetchError.response?.status === 403
    ) {
      navigate('/instances');
    }
  }, [hasUserFetchError, userFetchError, navigate]);

  useEffect(() => {
    if (!user) return;
    Sentry.setUser({ id: user.id, email: user.email });
    Sentry.setTags({
      customer_id: user.instance?.customer_id ?? null,
      instance_id: user.instance?.instance_id ?? null,
      product_sku: user.instance?.product_sku ?? null,
      hosted_region: user.instance?.hosted_region ?? null,
    });
  }, [user]);

  if (hasUserFetchError) {
    if (userFetchError instanceof AxiosError && userFetchError.response?.status === 403) {
      return <FullScreenLoader />;
    }
    return (
      <AccessDenied
        errorMessage={
          userFetchError instanceof AxiosError
            ? userFetchError.response?.data?.detail
            : userFetchError?.message
        }
        clear
        unauthorized={
          userFetchError instanceof AxiosError && userFetchError.response?.status === 401
        }
      />
    );
  }

  if (!authState || !authState.isAuthenticated || userLoading || !user || !opalAuth) {
    return <FullScreenLoader />;
  }

  return <AtomsHydrator initialValues={[[opalUserAtom, user]]}>{children}</AtomsHydrator>;
};

export default RequireAuth;
